import React from 'react';
import { observer } from 'mobx-react-lite';
import { Icon } from '../Icon';
import { useStore } from '@store/StoreProvider';

export const HowSection: React.FC = observer(() => {
  const { i18n } = useStore();
  const t = (key: string) => i18n.t(key);
  const steps = [
    { n: '01', icon: 'ic-user', title: t('auth.register.cta'), desc: t('landing.features.quick_signup') },
    { n: '02', icon: 'ic-plus-circle', title: t('channels.add_channel'), desc: t('channels.add_channel_hint') },
    { n: '03', icon: 'ic-users', title: t('team.invite_operators'), desc: t('team.invite_hint') },
    { n: '04', icon: 'ic-plug', title: t('widget.install.button'), desc: t('landing.steps.connect_text') },
    { n: '05', icon: 'ic-message', title: t('chat.accept_dialog'), desc: t('landing.features.inbox_text') },
  ];
  return (
  <section id="how" className="relative py-14 sm:py-20 overflow-hidden">
    <div className="land-desktop-block absolute inset-0 pointer-events-none">
      <div className="floaty absolute left-[3%] top-24 w-14 h-14 rounded-2xl border border-brand2/15" />
      <div className="floaty rev absolute right-[4%] top-1/3 w-9 h-9 rotate-45 border border-cyan-400/15" />
      <div className="floaty slow absolute left-[6%] bottom-20 w-7 h-7 rounded-full border border-indigo-400/20" />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <div className="reveal inline-flex items-center gap-2 text-xs font-semibold text-brand2 uppercase tracking-widest mb-4">
          <span className="w-8 h-px bg-brand2" /> {t('landing.how_it_works')} <span className="w-8 h-px bg-brand2" />
        </div>
        <h2 className="reveal text-3xl sm:text-5xl font-extrabold">{t('landing.channels.title')} <span className="text-gradient">{t('landing.features.one_console')}</span></h2>
        <p className="reveal mt-4 text-slate-400 text-lg">
          {t('landing.telegram.client_unaware')}
        </p>
      </div>

      <div className="reveal relative rounded-3xl border border-white/10 glass p-6 sm:p-10 mb-16 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative grid lg:grid-cols-[1fr_auto_1.2fr_auto_1fr] items-center gap-6 lg:gap-4 text-center">
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t('landing.flow.customers_write')}</div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-left">
              <span className="ic-badge shrink-0" style={{ width: 38, height: 38 }}><Icon id="ic-monitor" className="ic ic-sm" /></span>
              <div><div className="font-semibold text-sm">{t('channels.types.widget_on_site')}</div><div className="text-xs text-slate-500">{t('landing.features.popup_chat')}</div></div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-brand/10 border border-brand/30 text-left">
              <span className="ic-badge shrink-0" style={{ width: 38, height: 38 }}><Icon id="ic-send" className="ic ic-sm" /></span>
              <div><div className="font-semibold text-sm">{t('channels.types.telegram_bot')}</div><div className="text-xs text-slate-400">{t('landing.flow.client_writes_bot')}</div></div>
            </div>
          </div>
          <div className="flow-arrow text-brand2 text-3xl lg:rotate-0 rotate-90 justify-self-center">→</div>
          <div className="relative">
            <div className="hub-glow absolute -inset-3 bg-brand/25 blur-2xl rounded-3xl" />
            <div className="relative rounded-2xl border border-brand/40 bg-gradient-to-br from-brand/15 to-indigo-600/10 p-5">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-gradient-to-br from-brand to-indigo-500 text-white font-extrabold text-xs">X</span>
                <span className="font-bold">{t('landing.unified_console')}</span>
              </div>
              <div className="space-y-2 text-left">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {t('chat.dialog_title')} #663 · {t('chat.tags.site')} ·{t('chat.status.in_progress')}</div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {t('chat.dialog_title')} #664 · telegram · {t('chat.tags.new')}</div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 text-xs"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {t('chat.dialog_title')} #665 · {t('chat.tags.site')} ·{t('chat.status.in_progress')}</div>
              </div>
              <div className="mt-3 text-[11px] text-slate-400">{t('landing.features.unified_window')}</div>
            </div>
          </div>
          <div className="flow-arrow text-brand2 text-3xl lg:rotate-0 rotate-90 justify-self-center">→</div>
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t('landing.features.operators_bots_reply')}</div>
            <div className="px-4 py-3 rounded-2xl bg-white/5 border border-white/10">
              <div className="flex -space-x-2 justify-center mb-2">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-brand to-indigo-500 ring-2 ring-deep grid place-items-center text-xs font-bold">A</span>
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 ring-2 ring-deep grid place-items-center text-xs font-bold">M</span>
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 ring-2 ring-deep grid place-items-center text-xs font-bold">K</span>
              </div>
              <div className="text-xs text-slate-400">{t('landing.features.operators_bots_reply')}<br />{t('landing.stats.from_anywhere')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-10 text-center">
        <h3 className="reveal text-2xl sm:text-3xl font-extrabold">{t('landing.launch_in_5_steps')}</h3>
        <p className="reveal mt-2 text-slate-400">{t('landing.features.no_dev_needed')}</p>
      </div>
      <div data-stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {steps.map((step) => (
          <div key={step.n} className="reveal step-card relative rounded-2xl border border-white/10 bg-panel/40 p-5">
            <div className="text-5xl font-extrabold text-white/10 absolute top-2 right-3">{step.n}</div>
            <div className="ic-badge mb-3"><Icon id={step.icon} /></div>
            <h4 className="font-bold mb-1.5">{step.title}</h4>
            <p className="text-sm text-slate-400">{step.desc}</p>
          </div>
        ))}
      </div>

      <div className="reveal mt-6 rounded-2xl border border-brand/25 bg-brand/5 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="ic-badge shrink-0"><Icon id="ic-sparkles" /></div>
        <div className="flex-1">
          <div className="font-semibold">{t('landing.steps.customize')}</div>
          <p className="text-sm text-slate-400 mt-1">{t('landing.features.customization_desc')}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{t('widget.appearance.colors_and_theme')}</span>
          <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{t('nav.regions')}</span>
          <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{t('landing.features.quick_phrases_geo')}</span>
        </div>
      </div>
    </div>
  </section>
  );
});
